import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Printer, HelpCircle } from 'lucide-react';

/**
 * CollapsibleSection - Expandable content sections for educational content
 * Used for integrating additional information without cluttering the main page
 */
const CollapsibleSection = ({ 
  title, 
  icon,
  children, 
  defaultOpen = false,
  printable = false,
  printContent = null,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handlePrint = (e) => {
    e.stopPropagation();
    const printWindow = window.open('', '_blank');
    const content = printContent || document.getElementById(`collapsible-content-${title.replace(/\s+/g, '-')}`);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title} - Uro2Go</title>
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              padding: 20px;
              max-width: 800px;
              margin: 0 auto;
              line-height: 1.6;
            }
            h1 { color: #0B5D6E; font-size: 24px; border-bottom: 2px solid #0B5D6E; padding-bottom: 10px; }
            h2, h3 { color: #333; margin-top: 20px; }
            ul, ol { margin-left: 20px; }
            li { margin-bottom: 8px; }
            .warning { background: #FEF3C7; padding: 12px; border-left: 4px solid #F59E0B; margin: 16px 0; }
            .emergency { background: #FEE2E2; padding: 12px; border-left: 4px solid #DC2626; margin: 16px 0; }
            .tip { background: #ECFDF5; padding: 12px; border-left: 4px solid #10B981; margin: 16px 0; }
            .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          ${content?.innerHTML || ''}
          <div class="footer">
            <p>Uro2Go Patient Education • Printed ${new Date().toLocaleDateString()}</p>
            <p>This information is for educational purposes only. Always follow your doctor's specific instructions.</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className={`collapsible-section ${className} ${isOpen ? 'open' : ''}`}>
      <div 
        className="collapsible-header"
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onKeyDown={(e) => e.key === 'Enter' && setIsOpen(!isOpen)}
      >
        <div className="collapsible-title">
          {icon && <span className="collapsible-icon">{icon}</span>}
          <span>{title}</span>
        </div>
        <div className="collapsible-actions">
          {printable && isOpen && (
            <button 
              className="print-btn"
              onClick={handlePrint}
              title="Print this section"
            >
              <Printer size={16} />
            </button>
          )}
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>
      
      {isOpen && (
        <div 
          className="collapsible-content"
          id={`collapsible-content-${title.replace(/\s+/g, '-')}`}
        >
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * FAQBox - Frequently Asked Questions display
 */
export const FAQBox = ({ questions }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="faq-box">
      <div className="faq-header">
        <HelpCircle size={18} />
        <span>Frequently Asked Questions</span>
      </div>
      <div className="faq-list">
        {questions.map((faq, index) => (
          <div key={index} className="faq-item">
            <button 
              className={`faq-question ${openIndex === index ? 'open' : ''}`}
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <span>{faq.q}</span>
              {openIndex === index ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {openIndex === index && (
              <div className="faq-answer">{faq.a}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * CalloutBox - Highlighted information boxes
 */
export const CalloutBox = ({ type = 'info', title, children }) => {
  return (
    <div className={`callout-box callout-${type}`}>
      {title && <strong className="callout-title">{title}</strong>}
      <div className="callout-content">{children}</div>
    </div>
  );
};

/**
 * StepList - Numbered step-by-step instructions
 */
export const StepList = ({ steps }) => {
  return (
    <ol className="step-list">
      {steps.map((step, index) => (
        <li key={index} className="step-item">
          <span className="step-number">{index + 1}</span>
          <div className="step-content">
            {typeof step === 'string' ? step : (
              <>
                <strong>{step.title}</strong>
                {step.detail && <p>{step.detail}</p>}
              </>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
};

export default CollapsibleSection;
